// The slip the play screen shares: the stake and multiplier the call buttons send, the side whose
// odds are on show, and the side a call button is pointing at (the chart draws its bust).

import type { TicketPreview } from "../../sdk";
import { prefs } from "../../state/prefs";
import { createStore } from "../../state/store";

export type Side = "up" | "down";

export type SlipState = {
  /** Dollars, as typed. */
  stake: string;
  leverage: number;
  /** Fund from winnings waiting in the payout queue. */
  useDebt: boolean;
  /** The side whose odds the slip shows. */
  focus: Side;
  /** The side a call button is hovered or focused on, for the chart. */
  aim: Side | null;
};

export const DEFAULT_LEVERAGE = 100;

export const slip = createStore<SlipState>({
  stake: prefs.get().stake,
  leverage: DEFAULT_LEVERAGE,
  useDebt: false,
  focus: "up",
  aim: null,
});

export function setSlip(next: Partial<SlipState>) {
  slip.set((state) => ({ ...state, ...next }));
}

/** Each side's preview as the slip stands, for the chart's survival band. */
export const previews = createStore<{ instrumentId: number; up: TicketPreview; down: TicketPreview } | null>(null);

/** The multiplier remembered per market. */
export function leverageFor(symbol: string, max: number): number {
  const kept = prefs.get().leverage[symbol] ?? DEFAULT_LEVERAGE;
  return Math.max(1, Math.min(max, Math.round(kept)));
}

export function rememberLeverage(symbol: string, leverage: number) {
  prefs.set((all) => ({ ...all, leverage: { ...all.leverage, [symbol]: leverage } }));
}

export function rememberStake(stake: string) {
  prefs.set((all) => ({ ...all, stake }));
}

/** The multipliers a stepper walks through, under the market's `max` (which is always one of them). */
export function leverageSteps(max: number): number[] {
  const steps = [10, 25, 50, 100, 250, 500, 1000].filter((step) => step < max);
  return [...steps, max];
}

/** The stakes a stepper walks through, in whole dollars. */
export const STAKE_STEPS = [10, 25, 50, 100, 250, 500, 1000] as const;
